import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import configService from '../services/config';
import {
  Modal, Card, CardContent, Typography, Box, Button, IconButton, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, CircularProgress
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

// ---------- Theme tokens (kept consistent with the rest of the app) ----------
const ACCENT = '#6c5ce7';
const ACCENT_DARK = '#5b4bc4';
const TEXT_DARK = '#1e293b';
const TEXT_MUTED = '#64748b';
const TEXT_MUTED_2 = '#475569';
const BORDER = '#e2e8f0';
const SURFACE_MUTED = '#f8fafc';

// ---------- Helpers ----------
const fmtDate = (iso) =>
  !iso ? '—' : new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const fmtCurrency = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '—';
  return `₹ ${Number(num).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// ---------- Main Component ----------
export default function LedgerReport() {
  const { company_id, userId } = useSelector((state) => state.globalVariables);

  // --- Ledger / Salary Slip States ---
  const [ledgerData, setLedgerData] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(true);
  const [openSlipModal, setOpenSlipModal] = useState(false);
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [slipLoading, setSlipLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);


  const fetchLedgerData = async () => {
    setLedgerLoading(true);
    try {
      const res = await configService.getCustomerLedger({
        company_id,
        customer_id: userId,
      });


      let records = [];
      if (Array.isArray(res)) {
        records = res;
      } else if (Array.isArray(res?.data)) {
        records = res.data;
      } else if (Array.isArray(res?.data?.data)) {
        records = res.data.data;
      }

      const mapped = records.map((r, i) => ({
        id: r.entity_number ? `${r.type}-${r.entity_number}-${i}` : i,
        date: fmtDate(r.date),
        type: r.type || '—',
        entityNumber: r.entity_number || '—',
        amount: r.amount,
        isCredit: !!(r.is_credit === true || r.is_credit === 1 || r.is_credit === '1'),
      }));

      setLedgerData(mapped);
    } catch (error) {
      console.error('❌ Fetch Ledger Error:', error);
      setLedgerData([]);
    } finally {
      setLedgerLoading(false);
    }
  };

  useEffect(() => {
    if (!userId) return;
    fetchLedgerData();
  }, [userId, company_id]);


  const handleViewSlip = async (row) => {

    setSelectedSlip(row);
    setOpenSlipModal(true);
    setSlipLoading(true);

    try {
      const res = await configService.getSalarySlip({
        company_id,
        employee_id: userId,
        entity_number: row.entityNumber,
        type: row.type,
      });

      let slipData = null;
      if (Array.isArray(res?.data?.data)) {
        slipData = res.data.data[0] || null;
      } else if (res?.data?.data && typeof res.data.data === 'object') {
        slipData = res.data.data;
      } else if (res?.data && typeof res.data === 'object') {
        slipData = res.data;
      }

      if (slipData) {

        setSelectedSlip((prev) => ({
          ...prev,
          ...slipData,

          date: slipData.date ? fmtDate(slipData.date) : prev.date,
          amount: slipData.amount !== undefined ? slipData.amount : prev.amount,
        }));
      }
    } catch (error) {
      console.error('❌ Fetch Salary Slip Error:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to load slip details.');
    } finally {
      setSlipLoading(false);
    }
  };

  const handleCloseSlipModal = () => {
    setOpenSlipModal(false);
    setSelectedSlip(null);
    setSlipLoading(false);
  };


  const handleDownloadSlip = async () => {
    if (!selectedSlip) return;

    if (selectedSlip.attachment_base64) {
      const mime = selectedSlip.attachment_mime || 'application/pdf';
      const ext = mime.includes('pdf') ? 'pdf' : (mime.split('/')[1] || 'bin');
      downloadBase64File(selectedSlip.attachment_base64, mime, `Salary-Slip-${selectedSlip.entityNumber || 'entry'}.${ext}`);
      return;
    }

    if (configService.downloadSalarySlip) {
      setDownloading(true);
      try {
        const res = await configService.downloadSalarySlip({
          company_id,
          employee_id: userId,
          entity_number: selectedSlip.entityNumber,
          type: selectedSlip.type,
        });

        const blob = res?.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Salary-Slip-${selectedSlip.entityNumber || 'entry'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        return;
      } catch (error) {
        console.error('❌ Download Salary Slip Error:', error);
        toast.error('Could not download PDF, downloading summary instead.');
      } finally {
        setDownloading(false);
      }
    }

    const html = `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Ledger Entry - ${selectedSlip.entityNumber || ''}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #1e293b; }
            h2 { margin-bottom: 4px; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            td, th { padding: 8px; border-bottom: 1px solid #e2e8f0; font-size: 14px; }
            .amount { font-weight: bold; font-size: 16px; }
          </style>
        </head>
        <body>
          <h2>Ledger Entry Details</h2>
          <p>${selectedSlip.date || ''}</p>
          <table>
            <tbody>
              <tr><td>Type</td><td style="text-align:right">${selectedSlip.type || '—'}</td></tr>
              <tr><td>Reference No</td><td style="text-align:right">${selectedSlip.entityNumber || '—'}</td></tr>
              <tr><td>Entry Type</td><td style="text-align:right">${selectedSlip.isCredit ? 'Credit' : 'Debit'}</td></tr>
              <tr class="amount"><td>Amount</td><td style="text-align:right">${fmtCurrency(selectedSlip.amount)}</td></tr>
            </tbody>
          </table>
        </body>
      </html>
    `;
    downloadTextFile(html, 'text/html', `Ledger-${selectedSlip.entityNumber || 'entry'}.html`);
  };

  // --- Small download utilities (no external libs needed) ---
  const downloadBase64File = (base64, mime, filename) => {
    const link = document.createElement('a');
    link.href = `data:${mime};base64,${base64}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadTextFile = (content, mime, filename) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>

      {/* ── My Ledger ── */}
      <Card sx={{ borderRadius: 4, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: `1px solid ${BORDER}` }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, color: TEXT_DARK, mb: 2.5 }}>
            <ReceiptLongIcon sx={{ color: ACCENT }} />
            My Ledger
          </Typography>

          {ledgerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: ACCENT }} />
            </Box>
          ) : ledgerData.length === 0 ? (
            <Typography variant="body2" sx={{ color: TEXT_MUTED, textAlign: 'center', py: 6 }}>
              No ledger records available.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, border: `1px solid ${BORDER}` }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: SURFACE_MUTED, fontWeight: 700, fontSize: 12, color: TEXT_MUTED_2, letterSpacing: 0.3 } }}>
                    <TableCell>DATE</TableCell>
                    <TableCell>TYPE</TableCell>
                    <TableCell>REFERENCE NO</TableCell>
                    <TableCell>AMOUNT</TableCell>
                    <TableCell align="center">VIEW</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ledgerData.map((row, index) => (
                    <TableRow key={row.id || index} hover>
                      <TableCell sx={{ fontWeight: 600, color: TEXT_DARK }}>{row.date}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize', color: TEXT_DARK }}>{row.type}</TableCell>
                      <TableCell sx={{ color: TEXT_DARK }}>{row.entityNumber}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: TEXT_DARK }}>{fmtCurrency(row.amount)}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleViewSlip(row)}
                          sx={{
                            color: ACCENT,
                            border: `1px solid ${BORDER}`,
                            borderRadius: 2,
                            '&:hover': { bgcolor: 'rgba(108, 92, 231, 0.08)', borderColor: ACCENT }
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* ── Ledger Entry Detail / Download Modal ── */}
      <Modal open={openSlipModal} onClose={handleCloseSlipModal} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, zIndex: 1200 }}>
        <Card sx={{ width: '100%', maxWidth: 480, bgcolor: '#ffffff', borderRadius: 4, color: TEXT_DARK, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: TEXT_DARK }}>
                Ledger Entry Details
              </Typography>
              <IconButton size="small" onClick={handleCloseSlipModal}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {slipLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress sx={{ color: ACCENT }} />
              </Box>
            ) : selectedSlip ? (
              <>
                <Typography variant="body2" sx={{ color: TEXT_MUTED, mb: 0.5 }}>
                  {selectedSlip.date}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: TEXT_MUTED_2 }}>Type</Typography>
                  <Typography variant="body2" sx={{ color: TEXT_DARK, textTransform: 'capitalize' }}>{selectedSlip.type}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: TEXT_MUTED_2 }}>Reference No</Typography>
                  <Typography variant="body2" sx={{ color: TEXT_DARK }}>{selectedSlip.entityNumber}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: TEXT_MUTED_2 }}>Entry Type</Typography>
                  <Chip
                    label={selectedSlip.isCredit ? 'Credit' : 'Debit'}
                    size="small"
                    color={selectedSlip.isCredit ? 'success' : 'error'}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                {selectedSlip.remarks && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: TEXT_MUTED_2 }}>Remarks</Typography>
                    <Typography variant="body2" sx={{ color: TEXT_DARK }}>{selectedSlip.remarks}</Typography>
                  </Box>
                )}

                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Amount</Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ACCENT }}>{fmtCurrency(selectedSlip.amount)}</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button onClick={handleCloseSlipModal} sx={{ color: TEXT_MUTED, textTransform: 'none' }}>Close</Button>
                  <Button
                    variant="contained"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleDownloadSlip}
                    disabled={downloading}
                    sx={{ bgcolor: ACCENT, color: '#fff', px: 3, textTransform: 'none', '&:hover': { bgcolor: ACCENT_DARK } }}
                  >
                    {downloading ? 'Downloading...' : 'Download'}
                  </Button>
                </Box>
              </>
            ) : (
              <Typography variant="body2" sx={{ color: TEXT_MUTED }}>No voucher data available.</Typography>
            )}
          </CardContent>
        </Card>
      </Modal>
    </Box>
  );
}